import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

import { API_BASE, T } from '../constants';
import { fmtDate, fmtTime, fmtPrice, mlColor, dirStyle, catColor, systemLabel } from '../helpers';
import GradeBadge        from './badges/GradeBadge';
import SystemBadge       from './badges/SystemBadge';
import PrefilterState    from './PrefilterState';
import VariantEvaluation from './VariantEvaluation';

/* ─────────────────────────────────────────────────────────────────────────────
 * DecisionDetail
 *
 * Full-page "why did this decision happen" view. Only Stage 0 and CAV1 have
 * working detail endpoints — helpers.detailHref returns null for every other
 * system, so this component is reached only with S0-* or CAV1 system_ids.
 *
 *   1. Verdict banner
 *   2. Primary reason
 *   3. Pre-filter state            — Stage-0 gate checklist
 *   4. Variant evaluation          — per-variant threshold introspection
 *   5. Pipeline trace              — stage-by-stage pass / fail / skip
 *   6. Signal context & Decision record
 *   7. Signal ages (freshness coloring)
 *   8. Trade outcome               — if GO and position exists
 *   9. Raw payload                 — collapsible JSONB dump
 * ──────────────────────────────────────────────────────────────────────────── */

const STAGE0_DETAIL_PATH = '/strategies/api/strategies/stage0_ab/decisions';
const CAV1_DETAIL_PATH   = '/strategies/api/strategies/cav1/decisions';

function buildDetailUrl(system, key) {
  if (system?.startsWith('S0-')) {
    return `${API_BASE}${STAGE0_DETAIL_PATH}/${encodeURIComponent(key)}/`;
  }
  // CAV1 fall-through (the only other supported system).
  return `${API_BASE}${CAV1_DETAIL_PATH}/${encodeURIComponent(key)}/`;
}

export default function DecisionDetail() {
  const { system, key } = useParams();
  const navigate = useNavigate();

  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const url = buildDetailUrl(system, key);
    axios.get(url)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.error || err.message || 'fetch failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [system, key]);

  if (loading) return <FullPageMessage icon="spinner" title="Loading decision" subtitle={`${systemLabel(system)} · ${key}`} />;
  if (error)   return <FullPageMessage icon="error"   title="Could not load decision" subtitle={error} onBack={() => navigate('/')} />;
  if (!data)   return null;

  if (!data.found) {
    return <FullPageMessage
      icon="missing"
      title="Decision not found"
      subtitle={`No ${systemLabel(data.system)} decision with key ${data.key}`}
      onBack={() => navigate('/')}
    />;
  }

  const { verdict, pipeline, raw, pre_filter, variant_evaluation } = data;
  // TIER strategies put the verdict in `action_label`, not `action`. Prefer it.
  const isTrade     = (verdict.action_label || verdict.action) === 'TRADE';
  const accent      = isTrade ? T.bull       : T.bear;
  const accentBg    = isTrade ? T.bullBg     : T.bearBg;
  const accentBdr   = isTrade ? T.bullBorder : T.bearBorder;

  return (
    <div style={styles.page} className="page-container">
      {/* Top bar */}
      <header style={styles.header}>
        <Link to="/" style={styles.backLink}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ verticalAlign: 'middle', marginRight: 6 }}>
            <path d="M10 3 L5 8 L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Dashboard
        </Link>
        <div style={styles.headerRight}>
          <SystemBadge systemId={verdict.system_id} />
          <span style={styles.headerMeta}>
            {fmtDate(verdict.signal_ts)} · {fmtTime(verdict.signal_ts)}
          </span>
        </div>
      </header>

      {/* 1. Verdict banner */}
      <section style={{ ...styles.verdictBanner, background: accentBg, borderColor: accentBdr }}>
        <div style={styles.verdictLeft}>
          <div style={{ ...styles.verdictAction, color: accent, textShadow: `0 0 32px ${accent}80` }}>
            {verdict.action}
          </div>
          <div style={styles.verdictBadges}>
            {verdict.direction && (
              <span className="badge" style={{ ...dirStyle(verdict.direction), fontSize: 11 }}>{verdict.direction}</span>
            )}
            {verdict.category && (
              <span className="badge" style={{
                background: `${catColor(verdict.category)}22`,
                border:     `1px solid ${catColor(verdict.category)}55`,
                color:      catColor(verdict.category),
                fontSize:   11,
              }}>{verdict.category}</span>
            )}
            {verdict.gate_blocked && (
              <span className="badge" style={{
                background: 'rgba(239,83,80,0.14)',
                border:     '1px solid rgba(239,83,80,0.45)',
                color:      T.bear,
                fontSize:   11,
              }}>Gate: {verdict.gate_blocked}</span>
            )}
            {verdict.phase1_applied && (
              <span className="badge" style={{
                background: 'rgba(240,185,11,0.14)',
                border:     '1px solid rgba(240,185,11,0.45)',
                color:      T.gold,
                fontSize:   11,
              }}>Phase 1: {verdict.phase1_applied}</span>
            )}
          </div>
        </div>
        <div style={styles.verdictRight}>
          <div style={styles.verdictStatRow}>
            <Stat label="Grade">           <GradeBadge grade={verdict.grade} /></Stat>
            <Stat label="ML Score" mono>   <span style={{ color: mlColor(verdict.ml_score), fontWeight: 700 }}>{Math.round(parseFloat(verdict.ml_score) || 0)}</span></Stat>
            <Stat label="Size"     mono>   {isTrade ? `${verdict.contracts}c` : '—'}</Stat>
            <Stat label="Stop"     mono>   {isTrade ? `${verdict.stop_pts}pt` : '—'}</Stat>
          </div>
        </div>
      </section>

      {/* 2. Primary reason */}
      {(verdict.block_reason || verdict.reason) && (
        <section style={styles.primaryReason}>
          <div style={styles.sectionLabel}>
            {isTrade ? 'Reason' : 'Block reason'}
          </div>
          <div style={{ ...styles.reasonText, color: isTrade ? T.text : T.bear }}>
            {verdict.block_reason || verdict.reason}
          </div>
          {verdict.category_reason && (
            <div style={styles.reasonSub}>
              <span style={styles.reasonSubLabel}>Category reason: </span>
              {verdict.category_reason}
            </div>
          )}
        </section>
      )}

      {/* 3. Pre-filter state */}
      <PrefilterState block={pre_filter} />

      {/* 4. Variant evaluation (renders only when block is supplied) */}
      <VariantEvaluation block={variant_evaluation} />

      {/* 5. Pipeline trace */}
      <section style={styles.section}>
        <h3 style={styles.sectionH}>Decision pipeline</h3>
        <div style={styles.pipelineGrid}>
          {pipeline.map(p => <PipelineStage key={p.stage} stage={p} />)}
        </div>
      </section>

      {/* 6. Grouped raw sections */}
      <div style={styles.twoCol}>
        {raw.candidate && (
          <KVSection title="Signal context" rows={buildCandidateRows(raw.candidate)} />
        )}
        <KVSection title="Decision record" rows={buildDecisionRows(raw.decision, verdict.system_id)} />
      </div>

      {/* 7. Signal ages */}
      {raw.candidate?.ages && Object.keys(raw.candidate.ages).length > 0 && (
        <AgesSection ages={raw.candidate.ages} />
      )}

      {/* 8. Trade outcome */}
      {raw.trade && (
        <TradeOutcomeSection trade={raw.trade} isTrade={isTrade} />
      )}

      {/* 9. Raw JSON dump */}
      <RawPayloadSection raw={raw} />
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
 * Subcomponents (unchanged from previous batch)
 * ═══════════════════════════════════════════════════════════════════════════ */

function Stat({ label, mono, children }) {
  return (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, fontFamily: mono ? 'Roboto Mono, monospace' : undefined }}>
        {children}
      </div>
    </div>
  );
}


function PipelineStage({ stage }) {
  const { status, label, detail, order } = stage;
  const cfg = {
    pass: { color: T.bull, bg: T.bullBg,  border: T.bullBorder,  symbol: '✓', text: 'Pass' },
    fail: { color: T.bear, bg: T.bearBg,  border: T.bearBorder,  symbol: '✗', text: 'Fail' },
    skip: { color: T.textMuted, bg: 'rgba(79,89,102,0.06)', border: 'rgba(79,89,102,0.22)', symbol: '–', text: 'Skip' },
    info: { color: T.blue, bg: T.blueBg,  border: T.blueBorder,  symbol: 'i', text: 'Info' },
  }[status] || { color: T.textMuted, bg: 'transparent', border: T.border, symbol: '?', text: '?' };

  return (
    <div style={{ ...styles.pipelineCell, background: cfg.bg, borderColor: cfg.border }}>
      <div style={styles.pipelineTop}>
        <div style={{ ...styles.pipelineOrder, color: cfg.color, borderColor: cfg.border }}>{order}</div>
        <div style={{ ...styles.pipelineSymbol, color: cfg.color, fontWeight: 800 }}>{cfg.symbol}</div>
        <div style={{ ...styles.pipelineStatus, color: cfg.color }}>{cfg.text}</div>
      </div>
      <div style={styles.pipelineLabel}>{label}</div>
      <div style={styles.pipelineDetail}>{detail}</div>
    </div>
  );
}


function KVSection({ title, rows }) {
  const visible = rows.filter(r => r.value !== undefined && r.value !== null && r.value !== '');
  if (visible.length === 0) return null;
  return (
    <section style={styles.section}>
      <h3 style={styles.sectionH}>{title}</h3>
      <div style={styles.kvList}>
        {visible.map(r => (
          <div key={r.label} style={styles.kvRow}>
            <div style={styles.kvLabel}>{r.label}</div>
            <div style={{ ...styles.kvValue, ...(r.style || {}) }}>
              {r.render ? r.render(r.value) : String(r.value)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}


function AgesSection({ ages }) {
  const entries = Object.entries(ages).sort(([a], [b]) => a.localeCompare(b));
  return (
    <section style={styles.section}>
      <h3 style={styles.sectionH}>
        Signal ages <span style={styles.sectionHint}>(minutes since last fire — 999 = never seen)</span>
      </h3>
      <div style={styles.agesGrid}>
        {entries.map(([k, v]) => {
          const n = parseFloat(v);
          const fresh = n >= 0 && n < 20;
          const never = n >= 999 || n < 0;
          const color = fresh ? T.bull : never ? T.textMuted : T.text;
          return (
            <div key={k} style={styles.agesCell}>
              <div style={styles.agesLabel}>{k}</div>
              <div style={{ ...styles.agesValue, color }}>{v}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


function TradeOutcomeSection({ trade }) {
  const open   = trade.status === 'OPEN';
  const closed = trade.status === 'CLOSED';
  const pl     = trade.pl_points;
  const plGood = pl > 0;
  const plBad  = pl < 0;
  const plColor = plGood ? T.bull : plBad ? T.bear : T.textDim;

  return (
    <section style={styles.section}>
      <h3 style={styles.sectionH}>
        Trade outcome <span style={styles.sectionHint}>· trade #{trade.id}</span>
      </h3>
      <div style={styles.tradeRow}>
        <Stat label="Status">
          <span style={{
            color: open ? T.blue : closed ? (plGood ? T.bull : T.bear) : T.textMuted,
            fontWeight: 700,
          }}>{trade.status}</span>
        </Stat>
        <Stat label="Contracts" mono>{trade.contracts ?? '—'}</Stat>
        <Stat label="Entry" mono>
          <div style={{ color: T.text }}>{fmtPrice(trade.entry_px)}</div>
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{fmtTime(trade.entry_ts)}</div>
        </Stat>
        <Stat label="Exit" mono>
          {closed ? (
            <>
              <div style={{ color: T.text }}>{fmtPrice(trade.exit_px)}</div>
              <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{fmtTime(trade.exit_ts)}</div>
            </>
          ) : <span style={{ color: T.textMuted }}>—</span>}
        </Stat>
        <Stat label="P/L points" mono>
          <span style={{ color: plColor, fontWeight: 700 }}>
            {pl == null ? '—' : `${pl > 0 ? '+' : ''}${pl}pt`}
          </span>
        </Stat>
        <Stat label="P/L USD" mono>
          <span style={{ color: plColor, fontWeight: 700 }}>
            {trade.pl_usd == null ? '—' : `${trade.pl_usd > 0 ? '+' : ''}$${trade.pl_usd}`}
          </span>
        </Stat>
        <Stat label="Hold" mono>
          {trade.hold_minutes == null ? '—' : `${trade.hold_minutes}min`}
        </Stat>
      </div>
      {trade.exit_reason && (
        <div style={styles.tradeExitReason}>
          Exit reason: <span style={{ color: T.text }}>{trade.exit_reason}</span>
        </div>
      )}
    </section>
  );
}


function RawPayloadSection({ raw }) {
  const [open, setOpen] = useState(false);

  const blocks = useMemo(() => {
    const out = {};
    if (raw.candidate) out.candidate = raw.candidate;
    if (raw.decision)  out.decision  = raw.decision;
    if (raw.trade)     out.trade     = raw.trade;
    if (raw.payload)   out.payload   = raw.payload;
    return out;
  }, [raw]);

  return (
    <section style={styles.section}>
      <button
        onClick={() => setOpen(o => !o)}
        style={styles.rawToggle}
        aria-expanded={open}
      >
        <span style={styles.rawCaret}>{open ? '▾' : '▸'}</span>
        <span>Raw data dump</span>
        <span style={styles.rawHint}>{open ? 'click to collapse' : 'click to expand'}</span>
      </button>
      {open && (
        <pre style={styles.rawPre}>{JSON.stringify(blocks, null, 2)}</pre>
      )}
    </section>
  );
}


function FullPageMessage({ icon, title, subtitle, onBack }) {
  return (
    <div style={styles.fullMsg}>
      <div style={styles.fullMsgInner}>
        {icon === 'spinner' && <div className="spinner" style={{ width: 24, height: 24 }} />}
        {icon === 'error' && (
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="18" stroke={T.bear} strokeWidth="2"/>
            <path d="M14 14 L26 26 M26 14 L14 26" stroke={T.bear} strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
        {icon === 'missing' && (
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="18" stroke={T.textDim} strokeWidth="2" strokeDasharray="4 3"/>
            <path d="M15 16 A 5 5 0 0 1 25 16 C 25 20 20 20 20 23 M20 28 L20 28" stroke={T.textDim} strokeWidth="2" strokeLinecap="round"/>
          </svg>
        )}
        <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginTop: 12 }}>{title}</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6, fontFamily: 'Roboto Mono, monospace' }}>{subtitle}</div>
        {onBack && (
          <button onClick={onBack} style={styles.fullMsgBack}>← Back to dashboard</button>
        )}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
 * KV-row builders
 * ═══════════════════════════════════════════════════════════════════════════ */

function buildCandidateRows(c) {
  return [
    { label: 'Source strategy', value: c.src },
    { label: 'Trigger',         value: c.trigger,
      render: v => <span className="mono" style={{ color: T.text }}>{v}</span> },
    { label: 'Entry price',     value: c.entry_px,
      render: v => <span className="mono" style={{ color: T.text }}>{fmtPrice(v)}</span> },
    { label: 'Day',             value: c.day },
    { label: 'Time of day',     value: c.hour != null ? `${String(c.hour).padStart(2,'0')}:${String(c.minute).padStart(2,'0')}` : null,
      render: v => <span className="mono">{v}</span> },
    { label: 'Entry timestamp', value: c.entry_ts ? fmtTime(c.entry_ts) : null,
      render: v => <span className="mono">{v}</span> },
    { label: 'Candidate id',    value: c.id,
      render: v => <span className="mono" style={{ color: T.textMuted }}>#{v}</span> },
  ];
}


function buildDecisionRows(d, systemId) {
  const base = [
    { label: 'Status',           value: d.status },
    { label: 'Category',         value: d.category,
      render: v => <span style={{ color: catColor(v) }}>{v}</span> },
    { label: 'Category reason',  value: d.category_reason },
    { label: 'Grade',            value: d.grade,
      render: v => <GradeBadge grade={v} /> },
    { label: 'Grade reason',     value: d.grade_reason },
    { label: 'RSI',              value: d.rsi != null ? parseFloat(d.rsi).toFixed(2) : null,
      render: v => <span className="mono">{v}</span> },
    { label: 'MACD histogram',   value: d.macd_hist != null ? parseFloat(d.macd_hist).toFixed(4) : null,
      render: v => <span className="mono">{v}</span> },
    { label: 'VWAP distance',    value: d.vwap_dist != null ? parseFloat(d.vwap_dist).toFixed(2) : null,
      render: v => <span className="mono">{v}</span> },
    { label: 'Size multiplier',  value: d.size_mult,
      render: v => <span className="mono">×{v}</span> },
    { label: 'Concentration',    value: d.concentration,
      render: v => <span className="mono">{v}</span> },
    { label: 'Tier',             value: d.tier_num || d.tier },
    { label: 'Final size',       value: d.final_size,
      render: v => <span className="mono" style={{ color: T.text, fontWeight: 700 }}>{v}c</span> },
    { label: 'Block reason',     value: d.block_reason,
      style: { color: T.bear } },
  ];

  if (systemId === 'P1-S1') {
    base.push(
      { label: 'Strategies fired', value: d.strategies_fired?.length ? d.strategies_fired.join(', ') : null,
        render: v => <span className="mono" style={{ color: T.text }}>{v}</span> },
      { label: 'Phase 1 applied',  value: d.phase1_applied },
      { label: 'Price',            value: d.price,
        render: v => <span className="mono">{fmtPrice(v)}</span> },
      { label: 'Contracts',        value: d.contracts,
        render: v => <span className="mono">{v}c</span> },
      { label: 'size_c',           value: d.size_c,
        render: v => <span className="mono">{v}</span> },
    );
  }

  return base;
}


/* ═══════════════════════════════════════════════════════════════════════════
 * Styles
 * ═══════════════════════════════════════════════════════════════════════════ */

const styles = {
  page: {
    minHeight: '100dvh',
    background: 'var(--tv-bg)',
    color: T.text,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: 'clamp(10px, 3vw, 14px)',
    overflow: 'auto',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    padding: '10px 16px',
    background: 'var(--tv-surface)',
    border: '1px solid var(--tv-border)',
    borderRadius: 4,
  },
  backLink: {
    fontSize: 12,
    color: T.textDim,
    textDecoration: 'none',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  headerMeta: {
    fontSize: 11,
    fontFamily: 'Roboto Mono, monospace',
    color: T.textMuted,
  },

  verdictBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
    padding: '20px 24px',
    borderRadius: 4,
    border: '1px solid',
  },
  verdictLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  verdictAction: {
    fontSize: 'clamp(32px, 10vw, 56px)',
    fontWeight: 800,
    fontFamily: 'Roboto Mono, monospace',
    letterSpacing: '0.08em',
    lineHeight: 1,
  },
  verdictBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  verdictRight: {},
  verdictStatRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
    gap: 24,
    alignItems: 'end',
  },

  stat: { display: 'flex', flexDirection: 'column', gap: 4 },
  statLabel: {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: T.textMuted,
  },
  statValue: { fontSize: 14 },

  primaryReason: {
    padding: '16px 20px',
    background: 'var(--tv-surface)',
    border: '1px solid var(--tv-border)',
    borderRadius: 4,
  },
  sectionLabel: {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: T.textMuted,
    marginBottom: 8,
  },
  reasonText: {
    fontSize: 15,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  reasonSub: {
    marginTop: 10,
    fontSize: 11,
    color: T.textDim,
    lineHeight: 1.5,
  },
  reasonSubLabel: { color: T.textMuted },

  section: {
    background: 'var(--tv-surface)',
    border: '1px solid var(--tv-border)',
    borderRadius: 4,
    padding: 14,
  },
  sectionH: {
    margin: 0,
    marginBottom: 10,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: T.textMuted,
  },
  sectionHint: {
    fontWeight: 400,
    letterSpacing: '0.03em',
    textTransform: 'none',
    color: T.textDim,
    fontSize: 10,
    marginLeft: 8,
  },

  pipelineGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 8,
  },
  pipelineCell: {
    padding: 12,
    borderRadius: 4,
    border: '1px solid',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minHeight: 110,
  },
  pipelineTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  pipelineOrder: {
    width: 22, height: 22,
    borderRadius: '50%',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    fontFamily: 'Roboto Mono, monospace',
  },
  pipelineSymbol: {
    fontSize: 18,
    lineHeight: 1,
    marginLeft: 'auto',
  },
  pipelineStatus: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  pipelineLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: T.text,
    letterSpacing: '0.02em',
  },
  pipelineDetail: {
    fontSize: 11,
    color: T.textDim,
    lineHeight: 1.45,
    fontFamily: 'Roboto Mono, monospace',
    wordBreak: 'break-word',
  },

  twoCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
    gap: 14,
  },
  kvList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  kvRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(110px, 140px) minmax(0, 1fr)',
    gap: 12,
    padding: '7px 0',
    borderBottom: `1px solid var(--tv-border)`,
    alignItems: 'center',
    fontSize: 12,
  },
  kvLabel: {
    color: T.textMuted,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  kvValue: {
    color: T.text,
    fontFamily: 'Roboto Mono, monospace',
    wordBreak: 'break-word',
  },

  agesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: 6,
  },
  agesCell: {
    padding: '8px 10px',
    background: 'var(--tv-bg)',
    border: '1px solid var(--tv-border)',
    borderRadius: 3,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 8,
  },
  agesLabel: {
    fontSize: 10,
    color: T.textMuted,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    fontFamily: 'Roboto Mono, monospace',
  },
  agesValue: {
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'Roboto Mono, monospace',
  },

  tradeRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: 16,
    alignItems: 'end',
  },
  tradeExitReason: {
    marginTop: 14,
    fontSize: 11,
    color: T.textMuted,
    fontStyle: 'italic',
  },

  rawToggle: {
    all: 'unset',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    padding: '4px 0',
    color: T.textDim,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  rawCaret: {
    fontFamily: 'Roboto Mono, monospace',
    color: T.textMuted,
    width: 14,
    textAlign: 'center',
  },
  rawHint: {
    marginLeft: 'auto',
    color: T.textMuted,
    fontSize: 9,
    fontWeight: 400,
    textTransform: 'none',
    letterSpacing: '0.03em',
  },
  rawPre: {
    marginTop: 10,
    background: 'var(--tv-bg)',
    border: '1px solid var(--tv-border)',
    borderRadius: 3,
    padding: 12,
    fontSize: 11,
    fontFamily: 'Roboto Mono, monospace',
    color: T.text,
    overflow: 'auto',
    maxHeight: 480,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },

  fullMsg: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--tv-bg)',
  },
  fullMsgInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: 32,
  },
  fullMsgBack: {
    marginTop: 20,
    padding: '8px 16px',
    background: 'transparent',
    border: `1px solid var(--tv-border)`,
    color: T.textDim,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    borderRadius: 3,
    cursor: 'pointer',
  },
};
